import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {useIntl} from 'react-intl';

import {AuthHelpers, useAuthHelper, useCustomerType} from '@salesforce/commerce-sdk-react';
import {SHOWROOMS_LISTING_PATH} from '@amblique/king-living/app/constants/site-config';
import {ensureArray, getCustomerInitials, getMyShowroomFromCookie} from '@amblique/king-living/app/utils/utils';
import {clearCustomizeSessionData} from '@amblique/king-living/app/utils/session-utils';
import BaseLink from '@amblique/king-living/app/components/link';
import {
  Box, Button, Flex, Image, Spinner, Stack, Text, useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';
import withRegistration from '@salesforce/retail-react-app/app/components/with-registration';
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer';
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site';
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation';

import {ChevronRightIcon2, HeaderAccountIcon, HeaderMapPinIcon} from '@amblique/king-living/app/components/icons';
import LocaleSelector from '@amblique/king-living/app/components/locale-selector';
import {
  buildMobileDrawerFooterLinks,
  getLinkText,
  getLinkUrl,
  getNavLinkAnchorProps,
  getUtilityLinkButtonVariant,
  isCustomerAccountAction,
  isGuestAccountAction,
  isLogoutAction,
  isShowroomFooterLink,
  isTypedUtilityButton,
} from '@amblique/king-living/app/utils/navigation-primary-tabs';

const ButtonWithRegistration = withRegistration(Button);

const hasSubmenu = (menuItem) => {
  return ensureArray(menuItem?.submenuCollection?.items).filter(Boolean).length > 0;
};

const FooterLinkIcon = ({link, fallback}) => {
  const url = link?.image?.url;

  if (url) {
    return <Image alt="" boxSize="20px" flexShrink={0} objectFit="contain" src={url} />;
  }

  if (fallback === 'showroom') {
    return <HeaderMapPinIcon boxSize="20px" />;
  }

  if (fallback === 'account') {
    return <HeaderAccountIcon boxSize="20px" />;
  }

  return null;
};

FooterLinkIcon.propTypes = {
  fallback: PropTypes.oneOf(['account', 'showroom']),
  link: PropTypes.object,
};

const isCtaUtilityLink = (link) => (
  isGuestAccountAction(link)
  || isCustomerAccountAction(link)
  || isLogoutAction(link)
  || isShowroomFooterLink(link)
);

export const MegaMenuPanel = ({
  menuItems = [],
  utilityLinks = [],
  promotionalLinks = [],
  onArrowClick,
  onMyAccountClick,
  onSignInNavigate,
  showFunctionalSections = true,
  transform = 'translateX(0)',
  zIndex = 1,
  ...props
}) => {
  const intl = useIntl();
  const {isRegistered} = useCustomerType();
  const {data: customer, isError: isCustomerError, isFetched: isCustomerFetched, isLoading: isCustomerLoading} = useCurrentCustomer();
  const {buildUrl, site} = useMultiSite();
  const navigate = useNavigation();
  const logout = useAuthHelper(AuthHelpers.Logout);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const firstCharsName = getCustomerInitials(customer);
  const isAccountAvatarLoading = isRegistered &&
    !firstCharsName &&
    !isCustomerError &&
    (isCustomerLoading || !isCustomerFetched);
  const hasLocaleSelector = (site?.l10n?.supportedLocales?.length || 0) > 1;

  const handleSignInClick = () => {
    onSignInNavigate?.();
  };

  const handleMyAccountClick = () => {
    onSignInNavigate?.();
    onMyAccountClick?.();
  };

  const handleLogoutClick = async () => {
    if (isSigningOut) {
      return;
    }

    setIsSigningOut(true);
    onSignInNavigate?.();
    clearCustomizeSessionData(site?.id);
    try {
      await logout.mutateAsync();
      navigate(buildUrl('/'), 'replace');
    } finally {
      setIsSigningOut(false);
    }
  };

  const handleShowroomClick = () => {
    onSignInNavigate?.();
    const myShowroom = getMyShowroomFromCookie(site?.id);

    if (myShowroom?.slug) {
      navigate(buildUrl(`/showroom/${myShowroom.slug}`));
      return;
    }

    navigate(buildUrl(SHOWROOMS_LISTING_PATH));
  };

  const handleArrowClick = (e, menuItem) => {
    e.preventDefault();
    e.stopPropagation();
    if (hasSubmenu(menuItem) && onArrowClick) {
      onArrowClick(menuItem);
    }
  };

  const styles = useMultiStyleConfig('MegaMenu', {isMobile: true});
  const accountFallback = intl.formatMessage({
    defaultMessage: 'My account',
    id: 'header.button.assistive_msg.my_account',
  });
  const guestAccountFallback = intl.formatMessage({
    defaultMessage: 'Sign up or login',
    id: 'mega_menu.sign_up_or_login',
  });
  const showroomFallback = intl.formatMessage({
    defaultMessage: 'Find a showroom',
    id: 'mega_menu.find_a_showroom',
  });
  const signOutFallback = intl.formatMessage({
    defaultMessage: 'Sign out',
    id: 'header.popover.action.sign_out',
  });
  const footerLinks = buildMobileDrawerFooterLinks({
    utilityLinks,
    isRegistered,
    accountLabel: accountFallback,
    signOutLabel: signOutFallback,
  });
  const utilityRowLinks = footerLinks.filter(isCtaUtilityLink);
  const extraRowLinks = footerLinks.filter((link) => !isCtaUtilityLink(link));
  const getFooterLabel = (link) => {
    if (isLogoutAction(link)) {
      return signOutFallback;
    }

    if (isRegistered && isCustomerAccountAction(link)) {
      return accountFallback;
    }

    if (isGuestAccountAction(link)) {
      return guestAccountFallback;
    }

    const cmsLabel = getLinkText(link);

    if (cmsLabel) {
      return cmsLabel;
    }

    if (isShowroomFooterLink(link)) {
      return showroomFallback;
    }

    if (isCustomerAccountAction(link)) {
      return accountFallback;
    }

    return '';
  };

  const renderFooterLink = (link) => {
    const href = getLinkUrl(link);
    const label = getFooterLabel(link);
    const key = link?.sys?.id || href || label;

    if (isGuestAccountAction(link)) {
      return (
        <BaseLink
          key={key}
          className="mega-menu-mobile__cta-button"
          {...getNavLinkAnchorProps(link, href || '/login')}
          aria-label={label}
          onClick={handleSignInClick}
          {...styles.mobileCtaButton}
        >
          <FooterLinkIcon fallback="account" link={link} />
          <Text as="span">{label}</Text>
        </BaseLink>
      );
    }

    if (isLogoutAction(link)) {
      return (
        <Button
          key={key}
          className="mega-menu-mobile__cta-button"
          type="button"
          variant="unstyled"
          aria-label={label}
          isDisabled={isSigningOut}
          onClick={handleLogoutClick}
          {...styles.mobileCtaButton}
        >
          <FooterLinkIcon link={link} />
          <Text as="span">{label}</Text>
        </Button>
      );
    }

    if (isCustomerAccountAction(link)) {
      return (
        <ButtonWithRegistration
          key={key}
          className="mega-menu-mobile__cta-button"
          type="button"
          variant="unstyled"
          aria-label={label}
          onClick={handleMyAccountClick}
          {...styles.mobileCtaButton}
        >
          <Box aria-hidden {...styles.mobileCtaAccountBadge}>
            {isAccountAvatarLoading ? (
              <Spinner aria-hidden color="currentColor" size="xs" thickness="1.5px" />
            ) : (
              firstCharsName
            )}
          </Box>
          <Text as="span">{label}</Text>
        </ButtonWithRegistration>
      );
    }

    if (isShowroomFooterLink(link)) {
      return (
        <Button
          key={key}
          className="mega-menu-mobile__cta-button"
          type="button"
          variant="unstyled"
          aria-label={label}
          onClick={handleShowroomClick}
          {...styles.mobileCtaButton}
        >
          <FooterLinkIcon fallback="showroom" link={link} />
          <Text as="span">{label}</Text>
        </Button>
      );
    }

    if (!label) {
      return null;
    }

    return (
      <BaseLink
        key={key}
        className="mega-menu-mobile__cta-button"
        {...getNavLinkAnchorProps(link, href)}
        aria-label={label}
        onClick={handleSignInClick}
        {...styles.mobileCtaButton}
      >
        <FooterLinkIcon link={link} />
        <Text as="span">{label}</Text>
      </BaseLink>
    );
  };

  const renderExtraFooterLink = (link) => {
    const href = getLinkUrl(link);
    const label = getFooterLabel(link);
    const key = link?.sys?.id || href || label;

    if (!label) {
      return null;
    }

    const navProps = getNavLinkAnchorProps(link, href);

    if (isTypedUtilityButton(link)) {
      const variant = getUtilityLinkButtonVariant(link);

      return (
        <BaseLink
          key={key}
          className={`link link--${variant} mega-menu-mobile__cta-typed-link`}
          variant={variant}
          aria-label={label}
          onClick={handleSignInClick}
          {...navProps}
          {...styles.mobileCtaTypedLink}
        >
          <FooterLinkIcon link={link} />
          <Text as="span" color="inherit">{label}</Text>
        </BaseLink>
      );
    }

    return (
      <BaseLink
        key={key}
        className="mega-menu-mobile__cta-extra-link"
        variant="base"
        aria-label={label}
        onClick={handleSignInClick}
        {...navProps}
        {...styles.mobileCtaExtraLink}
      >
        {label}
      </BaseLink>
    );
  };

  return (
    <Box
      className="mega-menu-panel__mobile"
      {...styles.mobileSubcategoryPanel}
      transform={transform}
      zIndex={zIndex}
      {...props}
      sx={{
        willChange: 'transform',
        ...props.sx,
      }}
    >
      <Box className="mega-menu-panel-content" {...styles.mobileSubcategoryPanelContent}>
        <Stack className="mega-menu-items-list mega-menu-items-list__mobile" spacing={0} {...styles.mobilePanelItemsList}>
          {ensureArray(menuItems).filter(Boolean).map((item) => {
            const hasSubmenuItems = hasSubmenu(item);
            const itemLink = item?.link;

            return (
              <Box key={item.sys?.id || item.title} className="mega-menu-item-parent-wrapper">
                {hasSubmenuItems ? (
                  <Flex
                    as="button"
                    className="mega-menu-item-parent__item"
                    type="button"
                    onClick={(e) => handleArrowClick(e, item)}
                    {...styles.mobileMenuItemParentWrapper}
                  >
                    <Text className="mega-menu-item-text" {...styles.menuItemText} {...styles.mobileMenuItemText}>
                      {item.title}
                    </Text>
                    <Box
                      as="span"
                      aria-hidden
                      className="mega-menu-item-icon-button"
                      {...styles.menuItemIconButton}
                    >
                      <ChevronRightIcon2 {...styles.menuItemIcon} />
                    </Box>
                  </Flex>
                ) : itemLink ? (
                  <BaseLink
                    className="mega-menu-item-parent__item"
                    {...getNavLinkAnchorProps(itemLink, itemLink.url || '#')}
                    width="100%"
                    onClick={handleSignInClick}
                    {...styles.mobileMenuItemParentWrapper}
                  >
                    <Text className="mega-menu-item-text" {...styles.menuItemText} {...styles.mobileMenuItemText}>
                      {item.title}
                    </Text>
                  </BaseLink>
                ) : (
                  <Text className="mega-menu-item-parent__item mega-menu-item-text" {...styles.menuItemText} {...styles.mobileMenuItemText}>
                    {item.title}
                  </Text>
                )}
              </Box>
            );
          })}
        </Stack>
        {promotionalLinks.length > 0 && (
          <Stack className="mega-menu-mobile__promo" spacing={0} {...styles.mobilePromoGroup}>
            {promotionalLinks.map((link) => {
              const href = getLinkUrl(link);
              const label = getLinkText(link);

              if (!label) {
                return null;
              }

              return href ? (
                <BaseLink
                  key={link?.sys?.id || href || label}
                  className="mega-menu-mobile__promo-link"
                  {...getNavLinkAnchorProps(link, href)}
                  onClick={handleSignInClick}
                  {...styles.mobilePromoLink}
                >
                  {label}
                </BaseLink>
              ) : (
                <Text key={link?.sys?.id || label} {...styles.mobilePromoLink}>
                  {label}
                </Text>
              );
            })}
          </Stack>
        )}
      </Box>
      {showFunctionalSections && (footerLinks.length > 0 || hasLocaleSelector) && (
        <Box
          {...styles.mobileCtaFooter}
          className="drawer-menu-footer mega-menu-mobile__cta"
        >
          {utilityRowLinks.length > 0 && (
            <Box className="mega-menu-mobile__cta-row" {...styles.mobileCtaRow}>
              {utilityRowLinks.map(renderFooterLink)}
            </Box>
          )}
          {extraRowLinks.length > 0 && (
            <Box className="mega-menu-mobile__cta-extra" {...styles.mobileCtaExtraRow}>
              {extraRowLinks.map(renderExtraFooterLink)}
            </Box>
          )}
          <LocaleSelector variant="mobile" {...styles.mobileDrawerLocaleSelector} />
        </Box>
      )}
    </Box>
  );
};

MegaMenuPanel.displayName = 'MegaMenuPanel';
MegaMenuPanel.propTypes = {
  category: PropTypes.object,
  menuItems: PropTypes.array,
  utilityLinks: PropTypes.array,
  promotionalLinks: PropTypes.array,
  onBackClick: PropTypes.func,
  onArrowClick: PropTypes.func,
  onMyAccountClick: PropTypes.func,
  onMyQuoteClick: PropTypes.func,
  onSignInNavigate: PropTypes.func,
  parentCategory: PropTypes.object,
  showFunctionalSections: PropTypes.bool,
  transform: PropTypes.string,
  zIndex: PropTypes.number,
  sx: PropTypes.object,
};
